/**
 * Voice recorder component.
 * Records audio using MediaRecorder API.
 */
import React, { useState, useRef, useEffect } from 'react';
import { Mic, Square, X, Play, Pause } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface VoiceRecorderProps {
  onRecorded: (audioBlob: Blob) => void;
  onClose: () => void;
}

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({ onRecorded, onClose }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm',
      });

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);

        // Arrêter le stream
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);

      // Démarrer le timer
      timerRef.current = setInterval(() => {
        setDuration((prev) => prev + 1);
      }, 1000);

      toast.success('Enregistrement démarré');
    } catch (error: any) {
      console.error('Error starting recording:', error);
      toast.error('Impossible d\'accéder au microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      toast.success('Enregistrement terminé');
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        setIsPaused(false);
        
        timerRef.current = setInterval(() => {
          setDuration((prev) => prev + 1);
        }, 1000);
      } else {
        mediaRecorderRef.current.pause();
        setIsPaused(true);
        
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      }
    }
  };

  const handleSend = () => {
    if (audioBlob) {
      onRecorded(audioBlob);
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const togglePlayback = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">
            Message vocal
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Visualisation */}
        <div className="mb-6">
          {isRecording ? (
            <div className="flex flex-col items-center space-y-4">
              <div className="relative">
                <div className={`w-24 h-24 rounded-full bg-red-100 flex items-center justify-center ${
                  !isPaused ? 'animate-pulse' : ''
                }`}>
                  <Mic className="h-12 w-12 text-red-600" />
                </div>
                {!isPaused && (
                  <div className="absolute inset-0 rounded-full border-4 border-red-600 animate-ping"></div>
                )}
              </div>
              
              <div className="text-2xl font-mono text-gray-900">
                {formatDuration(duration)}
              </div>
              
              <div className="flex items-center space-x-2">
                <span className={`w-3 h-3 rounded-full ${
                  isPaused ? 'bg-yellow-500' : 'bg-red-600 animate-pulse'
                }`}></span>
                <span className="text-sm text-gray-600">
                  {isPaused ? 'En pause' : 'Enregistrement en cours...'}
                </span>
              </div>
            </div>
          ) : audioUrl ? (
            <div className="flex flex-col items-center space-y-4">
              <div className="w-full bg-gray-100 rounded-lg p-4">
                <audio
                  ref={audioRef}
                  src={audioUrl}
                  onEnded={() => setIsPlaying(false)}
                  className="hidden"
                />
                
                <div className="flex items-center space-x-4">
                  <button
                    onClick={togglePlayback}
                    className="p-3 bg-purple-600 text-white rounded-full hover:bg-purple-700"
                  >
                    {isPlaying ? (
                      <Pause className="h-5 w-5" />
                    ) : (
                      <Play className="h-5 w-5" />
                    )}
                  </button>
                  
                  <div className="flex-1">
                    <div className="h-2 bg-gray-300 rounded-full overflow-hidden">
                      <div className="h-full bg-purple-600 w-0"></div>
                    </div>
                  </div>
                  
                  <span className="text-sm text-gray-600 font-mono">
                    {formatDuration(duration)}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center space-y-4 py-8">
              <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center">
                <Mic className="h-12 w-12 text-gray-400" />
              </div>
              <p className="text-gray-500 text-center">
                Cliquez sur le bouton pour commencer l'enregistrement
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-center space-x-3">
          {!isRecording && !audioBlob && (
            <button
              onClick={startRecording}
              className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
            >
              <Mic className="h-5 w-5" />
              <span>Démarrer</span>
            </button>
          )}

          {isRecording && (
            <>
              <button
                onClick={pauseRecording}
                className="px-6 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
              >
                {isPaused ? 'Reprendre' : 'Pause'}
              </button>
              
              <button
                onClick={stopRecording}
                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center space-x-2"
              >
                <Square className="h-5 w-5" />
                <span>Arrêter</span>
              </button>
            </>
          )}

          {audioBlob && (
            <>
              <button
                onClick={() => {
                  setAudioBlob(null);
                  setAudioUrl(null);
                  setDuration(0);
                }}
                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Recommencer
              </button>
              
              <button
                onClick={handleSend}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Envoyer
              </button>
            </>
          )}
        </div>

        {/* Info */}
        <p className="mt-4 text-xs text-gray-500 text-center">
          Durée maximale recommandée : 2 minutes
        </p>
      </div>
    </div>
  );
};

export default VoiceRecorder;
